import React, { useMemo } from "react";
import { Box, Autocomplete, TextField, Chip } from "@mui/material";
import { AUTOCOMPLETE_DROPDOWN_SLOT_PROPS } from "../../../theme/filterstyles";
import { COUNTRY_MAP } from "../../../utils/Countries";
import {
  buildNormalizedOptions,
  normalizeCity,
} from "../../../utils/filterNormalization";

const BRAND_BLUE = "#00336C";

const CHIP_SX = {
  backgroundColor: BRAND_BLUE,
  color: "white",
  fontWeight: 600,
  height: 22,
  "& .MuiChip-label": { px: 0.75 },
  "& .MuiChip-deleteIcon": {
    color: "white",
    "&:hover": { color: "white", opacity: 0.85 },
  },
};

export default function SiteConsultantFilters({
  consultants = [],
  filterOptions,
  industryConsultants,
  countryFilter = [],
  setCountryFilter,
  stateFilter = [],
  setStateFilter,
  cityFilter = [],
  setCityFilter,
  industryFilter = [],
  setIndustryFilter,
}) {
  const FILTER_FIELD_SX = (minWidth = 220) => ({
    width: { xs: "100%", sm: `min(100%, ${minWidth}px)`, md: "auto" },
    minWidth: { xs: "100%", sm: minWidth, md: minWidth },
    flex: { xs: "1 1 100%", sm: `1 1 ${minWidth}px`, md: `1 1 ${minWidth}px` },
  });

  /* ===========================
     LOCATION INDEX (NEW)
     Builds cascading filter map
  ============================ */
  const locationIndex = useMemo(() => {
    if (filterOptions?.location_index) {
      return filterOptions.location_index;
    }

    const index = {};

    consultants.forEach((c) => {
      const country = c.country;
      const state = c.state;
      const city = normalizeCity(c.city);

      if (!country) return;

      if (!index[country]) index[country] = {};

      if (state) {
        if (!index[country][state]) index[country][state] = new Set();
        if (city) index[country][state].add(city);
      }
    });

    // convert Sets → arrays
    Object.keys(index).forEach((country) => {
      Object.keys(index[country]).forEach((state) => {
        index[country][state] = [...index[country][state]].sort((a, b) =>
          a.localeCompare(b),
        );
      });
    });

    return index;
  }, [consultants, filterOptions]);

  /* ===========================
     COUNTRY OPTIONS
  ============================ */
  const countryOptions = useMemo(() => {
    return Object.keys(locationIndex).sort();
  }, [locationIndex]);

  const isNorthAmericaSelected =
    countryFilter.includes("United States") || countryFilter.includes("Canada");

  /* ===========================
     STATE OPTIONS
  ============================ */
  const stateOptions = useMemo(() => {
    if (!countryFilter.length) return [];

    const map = new Map();

    countryFilter.forEach((country) => {
      const states = Object.keys(locationIndex[country] || {});

      states.forEach((stateName) => {
        map.set(stateName, {
          code: stateName,
          label: stateName,
          group: country,
        });
      });
    });

    return Array.from(map.values()).sort((a, b) => {
      if (a.group !== b.group) return a.group.localeCompare(b.group);
      return a.label.localeCompare(b.label);
    });
  }, [countryFilter, locationIndex]);

  /* ===========================
     CITY OPTIONS
  ============================ */
  const cityOptions = useMemo(() => {
    if (!countryFilter.length) return [];

    const cities = new Set();

    countryFilter.forEach((country) => {
      if (stateFilter.length) {
        stateFilter.forEach((state) => {
          (locationIndex[country]?.[state] || []).forEach((city) =>
            cities.add(city),
          );
        });
        return;
      }

      Object.values(locationIndex[country] || {}).forEach((countryCities) => {
        countryCities.forEach((city) => cities.add(city));
      });
    });

    return [...cities].sort((a, b) => a.localeCompare(b));
  }, [countryFilter, stateFilter, locationIndex]);

  /* ===========================
     INDUSTRY OPTIONS
  ============================ */
  const industryOptions = useMemo(() => {
    const sourceConsultants = Array.isArray(industryConsultants)
      ? industryConsultants
      : consultants;

    if (Array.isArray(filterOptions?.industries)) {
      return filterOptions.industries;
    }

    return buildNormalizedOptions(
      sourceConsultants.flatMap((c) =>
        Array.isArray(c.industries_list) ? c.industries_list : []
      ),
    );
  }, [consultants, filterOptions, industryConsultants]);

  /* ===========================
     CITY DISABLE LOGIC
  ============================ */
  const isCityDisabled = useMemo(() => {
    if (!countryFilter.length) return true;

    const northAmericaSelected =
      countryFilter.includes("United States") || countryFilter.includes("Canada");

    if (northAmericaSelected && !stateFilter.length) return true;

    return false;
  }, [countryFilter, stateFilter]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        flexWrap: { xs: "nowrap", sm: "wrap" },
        gap: { xs: 1, sm: 1.5, md: 2 },
        mb: 2,
        alignItems: { xs: "stretch", sm: "flex-start" },
        width: "100%",
        maxWidth: 1800,
        mx: "auto",
      }}
    >
      {/* COUNTRY */}
      <Autocomplete
        multiple
        limitTags={1}
        size="small"
        options={countryOptions}
        value={countryFilter}
        onChange={(_, values) => {
          setCountryFilter(values ?? []);
          setStateFilter([]);
          setCityFilter([]);
        }}
        slotProps={AUTOCOMPLETE_DROPDOWN_SLOT_PROPS}
        sx={FILTER_FIELD_SX(220)}
        getOptionLabel={(opt) => COUNTRY_MAP[opt] || opt}
        renderTags={(value, getTagProps) => {
          if (!value.length) return null;
          const extra = value.length - 1;
          const first = COUNTRY_MAP[value[0]] || value[0];
          return [
            <Chip
              {...getTagProps({ index: 0 })}
              size="small"
              label={extra > 0 ? `${first} +${extra}` : first}
              sx={CHIP_SX}
            />,
          ];
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Country"
            InputLabelProps={{ ...params.InputLabelProps, sx: { whiteSpace: "nowrap" } }}
          />
        )}
      />

      {/* STATE / PROVINCE */}
      <Autocomplete
        multiple
        limitTags={1}
        size="small"
        options={stateOptions}
        value={stateOptions.filter((o) => stateFilter.includes(o.code))}
        disabled={!isNorthAmericaSelected}
        groupBy={(opt) => opt.group || ""}
        getOptionLabel={(opt) => opt.label}
        isOptionEqualToValue={(opt, val) => opt.code === val.code}
        onChange={(_, values) => {
          const codes = (values ?? []).map((v) => v.code);
          setStateFilter(codes);
          setCityFilter([]);
        }}
        slotProps={AUTOCOMPLETE_DROPDOWN_SLOT_PROPS}
        sx={FILTER_FIELD_SX(260)}
        renderTags={(value, getTagProps) => {
          if (!value.length) return null;
          const extra = value.length - 1;
          const first = value[0]?.label || "";
          return [
            <Chip
              {...getTagProps({ index: 0 })}
              size="small"
              label={extra > 0 ? `${first} +${extra}` : first}
              sx={CHIP_SX}
            />,
          ];
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="State / Province"
            placeholder={
              !isNorthAmericaSelected ? "Select US or Canada first" : ""
            }
            InputLabelProps={{ ...params.InputLabelProps, sx: { whiteSpace: "nowrap" } }}
          />
        )}
      />

      {/* CITY */}
      <Autocomplete
        multiple
        limitTags={1}
        size="small"
        options={cityOptions}
        value={cityFilter}
        disabled={isCityDisabled}
        onChange={(_, values) => setCityFilter(values ?? [])}
        slotProps={AUTOCOMPLETE_DROPDOWN_SLOT_PROPS}
        sx={FILTER_FIELD_SX(220)}
        renderTags={(value, getTagProps) => {
          if (!value.length) return null;
          const extra = value.length - 1;
          return [
            <Chip
              {...getTagProps({ index: 0 })}
              size="small"
              label={extra > 0 ? `${value[0]} +${extra}` : value[0]}
              sx={CHIP_SX}
            />,
          ];
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="City"
            placeholder={
              !countryFilter.length
                ? "Select a country first"
                : (countryFilter.includes("United States") ||
                      countryFilter.includes("Canada")) &&
                    !stateFilter.length
                  ? "Select a state/province first"
                  : ""
            }
            InputLabelProps={{ ...params.InputLabelProps, sx: { whiteSpace: "nowrap" } }}
          />
        )}
      />

      {/* INDUSTRY */}
      <Autocomplete
        multiple
        limitTags={1}
        size="small"
        options={industryOptions}
        value={industryFilter}
        onChange={(_, values) => setIndustryFilter(values ?? [])}
        slotProps={AUTOCOMPLETE_DROPDOWN_SLOT_PROPS}
        sx={FILTER_FIELD_SX(220)}
        renderTags={(value, getTagProps) => {
          if (!value.length) return null;
          const extra = value.length - 1;
          return [
            <Chip
              {...getTagProps({ index: 0 })}
              size="small"
              label={extra > 0 ? `${value[0]} +${extra}` : value[0]}
              sx={CHIP_SX}
            />,
          ];
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Industry"
            InputLabelProps={{ ...params.InputLabelProps, sx: { whiteSpace: "nowrap" } }}
          />
        )}
      />
    </Box>
  );
}
